import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Alert, Snackbar, IconButton, Tooltip, Table, TableHead, TableRow, TableCell, TableBody, Divider, Link, Box,
} from '@mui/material';
import { EpcProjectSelect } from '../../components/EpcProjectSelect';
import { Add, Delete, ShoppingCart, LocalShipping } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcPoApi, epcRequisitionApi, type EpcPurchaseOrder, type PoItem } from '../../api/epcProcurement.api';
import { epcBoqApi } from '../../api/epcBoq.api';
import { FileUploadField } from '../../components/FileUploadField';
import { vendorsApi } from '../../api/vendors.api';
import { clientsApi } from '../../api/clients.api';
import { projectsApi } from '../../api/projects.api';
import { sitesApi } from '../../api/sites.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

interface LpoLine { description: string; unit: string; quantity: string; rate: string }
const emptyLpoLine = (): LpoLine => ({ description: '', unit: 'nos', quantity: '1', rate: '' });

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'received' ? 'success' : s === 'issued' ? 'info' : s === 'partially_received' ? 'warning' : s === 'cancelled' ? 'error' : 'default';
const lpoStageColor = (s?: string | null): 'default' | 'info' | 'success' | 'error' | 'warning' => {
  if (!s) return 'default';
  if (s === 'Request Completed') return 'success';
  if (s === 'Invoice Verify') return 'info';
  if (s.startsWith('Payment Released')) return 'info';
  if (s.startsWith('Payment Approved')) return 'warning';
  if (s === 'Payment Request Raised' || s === 'Pending Approval') return 'warning';
  return 'default';
};

export function EpcPurchaseOrdersPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [projectId, setProjectId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const pid = projectId ? Number(projectId) : undefined;
  const { data, isLoading } = useQuery({ queryKey: ['epc-pos', page, limit, pid], queryFn: () => epcPoApi.list({ page, limit, projectId: pid }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-pos'] });

  const columns: Column<EpcPurchaseOrder>[] = [
    { key: 'poCode', label: 'LPO', render: (r) => r.poCode },
    { key: 'supplierName', label: 'Supplier / Shop', render: (r) => r.supplierName ?? r.vendorName ?? (r.vendorId ? `#${r.vendorId}` : '—') },
    { key: 'projectId', label: 'Project', render: (r) => `#${r.projectId}` },
    { key: 'totalAmount', label: 'Total', align: 'right', render: (r) => inr(r.totalAmount) },
    { key: 'lpoStage', label: 'Stage', render: (r) => <Chip size="small" label={r.lpoStage ?? r.status} color={lpoStageColor(r.lpoStage)} /> },
    { key: 'status', label: 'GRN', render: (r) => <Chip size="small" variant="outlined" label={r.status.replace('_', ' ')} color={statusColor(r.status)} /> },
  ];

  return (
    <>
      <PageHeader title="Local Purchase Orders (LPO)" subtitle="Local purchases from a shop / retail counter (or an onboarded vendor) — mapped to a site, with goods receipt."
        action={hasPermission('epc_purchase_order.create') && (
          <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setCreateOpen(true); }}>New LPO</Button>
        )} />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <EpcProjectSelect value={projectId} onChange={(v) => { setProjectId(v); setPage(1); }} helperText={pid ? undefined : 'Select a project'} />
      </Stack>

      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><ShoppingCart fontSize="small" /></IconButton></Tooltip>} />

      {createOpen && <CreateDialog onClose={() => setCreateOpen(false)}
        onDone={(id) => { setCreateOpen(false); invalidate(); setDetailId(id); }} onError={setError} error={error} />}
      {detailId && <DetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}

      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}

function CreateDialog({ onClose, onDone, onError, error }: {
  onClose: () => void; onDone: (id: number) => void; onError: (e: string) => void; error: string | null;
}) {
  const [clientId, setClientId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [siteId, setSiteId] = useState<number | ''>('');
  const [shop, setShop] = useState({ name: '', contact: '', phone: '', address: '', city: '', state: '', region: '', pincode: '', gstin: '' });
  const [vendorId, setVendorId] = useState('');
  const [requisitionId, setRequisitionId] = useState('');
  const [boqId, setBoqId] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [quotationUrl, setQuotationUrl] = useState('');
  const [lines, setLines] = useState<LpoLine[]>([emptyLpoLine()]);

  const clients = useQuery({ queryKey: ['clients', 'active'], queryFn: () => clientsApi.list({ limit: 500, status: 'active' }) });
  const projects = useQuery({ queryKey: ['projects', 'all'], queryFn: () => projectsApi.list({ limit: 1000 }) });
  const sites = useQuery({ queryKey: ['sites', 'all'], queryFn: () => sitesApi.list({ limit: 1000 }) });
  const { data: vendors } = useQuery({ queryKey: ['vendors', 'active'], queryFn: () => vendorsApi.list({ limit: 500, status: 'active' }) });
  const { data: reqs } = useQuery({
    queryKey: ['epc-requisitions', 'accepted', projectId],
    queryFn: () => epcRequisitionApi.list({ projectId: Number(projectId), status: 'approved', procurementStatus: 'accepted', limit: 100 }),
    enabled: Boolean(projectId),
  });
  // Site-mapped BOQs to optionally seed materials from.
  const { data: boqs } = useQuery({
    queryKey: ['epc-boqs', 'for-site', siteId],
    queryFn: () => epcBoqApi.list({ siteId: Number(siteId), limit: 100 }),
    enabled: Boolean(siteId),
  });
  const clientProjects = (projects.data?.data ?? []).filter((p) => p.clientId === clientId);
  const projectSites = (sites.data?.data ?? []).filter((s) => s.projectId === projectId);
  const setLine = (i: number, k: keyof LpoLine, v: string) => setLines((arr) => arr.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const seeded = Boolean(requisitionId) || Boolean(boqId); // items come from the source, not manual entry

  const mut = useMutation({
    mutationFn: () => epcPoApi.create({
      projectId: Number(projectId), siteId: siteId ? Number(siteId) : undefined,
      boqId: boqId ? Number(boqId) : undefined,
      vendorId: vendorId ? Number(vendorId) : undefined,
      requisitionId: requisitionId ? Number(requisitionId) : undefined,
      shopName: shop.name.trim() || undefined, shopContact: shop.contact.trim() || undefined, shopPhone: shop.phone.trim() || undefined,
      shopAddress: shop.address.trim() || undefined, shopCity: shop.city.trim() || undefined, shopState: shop.state.trim() || undefined,
      shopRegion: shop.region.trim() || undefined, shopPincode: shop.pincode.trim() || undefined, shopGstin: shop.gstin.trim() || undefined,
      quotationUrl: quotationUrl || undefined,
      expectedDate: expectedDate || undefined,
      items: seeded ? undefined : lines.filter((l) => l.description.trim()).map((l) => ({ description: l.description.trim(), unit: l.unit || undefined, quantity: Number(l.quantity) || 0, rate: Number(l.rate) || 0 })),
    }),
    onSuccess: (po) => onDone(po.id),
    onError: (e) => onError(extractApiError(e)),
  });

  const validLines = lines.filter((l) => l.description.trim());
  const total = validLines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.rate) || 0), 0);
  const canSave = Boolean(projectId) && (Boolean(shop.name.trim()) || Boolean(vendorId) || Boolean(requisitionId)) && (seeded || validLines.length > 0);

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>New Local Purchase Order</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Client → Project → Site</Typography></Divider>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Client *" value={clientId} sx={{ minWidth: 220 }}
              onChange={(e) => { setClientId(e.target.value ? Number(e.target.value) : ''); setProjectId(''); setSiteId(''); setRequisitionId(''); setBoqId(''); }}>
              {(clients.data?.data ?? []).map((c) => <MenuItem key={c.id} value={c.id}>{c.clientName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Project *" value={projectId} disabled={!clientId} sx={{ minWidth: 240 }}
              onChange={(e) => { setProjectId(e.target.value ? Number(e.target.value) : ''); setSiteId(''); setRequisitionId(''); setBoqId(''); }}>
              {clientProjects.map((p) => <MenuItem key={p.id} value={p.id}>{p.projectCode} — {p.projectName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Site" value={siteId} disabled={!projectId} sx={{ minWidth: 220 }}
              onChange={(e) => { setSiteId(e.target.value ? Number(e.target.value) : ''); setBoqId(''); }}>
              {projectSites.map((s) => <MenuItem key={s.id} value={s.id}>{s.siteName} ({s.siteCode})</MenuItem>)}
            </TextField>
            <TextField select size="small" label="BOQ (site-mapped)" value={boqId} disabled={!siteId} sx={{ minWidth: 240 }}
              onChange={(e) => setBoqId(e.target.value)} helperText={siteId ? 'Seed materials from the site BOQ' : 'Select a site first'}>
              <MenuItem value="">— none —</MenuItem>
              {(boqs?.data ?? []).map((b) => <MenuItem key={b.id} value={b.id}>{b.boqCode}{b.title ? ` — ${b.title}` : ''}</MenuItem>)}
            </TextField>
          </Stack>

          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Purchased from — shop / retail counter</Typography></Divider>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="Shop / Counter name" value={shop.name} onChange={(e) => setShop({ ...shop, name: e.target.value })} sx={{ minWidth: 220 }} helperText="Or pick an onboarded vendor below" />
            <TextField size="small" label="Contact person" value={shop.contact} onChange={(e) => setShop({ ...shop, contact: e.target.value })} sx={{ minWidth: 160 }} />
            <TextField size="small" label="Phone" value={shop.phone} onChange={(e) => setShop({ ...shop, phone: e.target.value })} sx={{ minWidth: 140 }} />
            <TextField size="small" label="GSTIN" value={shop.gstin} onChange={(e) => setShop({ ...shop, gstin: e.target.value.toUpperCase() })} sx={{ minWidth: 160 }} />
            <TextField size="small" label="Address" value={shop.address} onChange={(e) => setShop({ ...shop, address: e.target.value })} sx={{ minWidth: 260, flex: 1 }} />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField size="small" label="City" value={shop.city} onChange={(e) => setShop({ ...shop, city: e.target.value })} sx={{ minWidth: 160 }} />
            <TextField size="small" label="State" value={shop.state} onChange={(e) => setShop({ ...shop, state: e.target.value })} sx={{ minWidth: 160 }} />
            <TextField size="small" label="Region" value={shop.region} onChange={(e) => setShop({ ...shop, region: e.target.value })} sx={{ minWidth: 160 }} />
            <TextField size="small" label="Pincode" value={shop.pincode} onChange={(e) => setShop({ ...shop, pincode: e.target.value })} sx={{ minWidth: 120 }} />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <TextField select size="small" label="Onboarded vendor (optional)" value={vendorId} onChange={(e) => setVendorId(e.target.value)} sx={{ minWidth: 240 }}>
              <MenuItem value="">— none (local shop) —</MenuItem>
              {(vendors?.data ?? []).map((v) => <MenuItem key={v.id} value={v.id}>{v.vendorName}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Seed from accepted requisition (optional)" value={requisitionId} onChange={(e) => setRequisitionId(e.target.value)} sx={{ minWidth: 260 }} disabled={!projectId || Boolean(boqId)}>
              <MenuItem value="">— none (enter materials below) —</MenuItem>
              {(reqs?.data ?? []).map((r) => <MenuItem key={r.id} value={r.id}>{r.requisitionCode}{r.title ? ` — ${r.title}` : ''}</MenuItem>)}
            </TextField>
            <TextField label="Expected delivery" type="date" size="small" InputLabelProps={{ shrink: true }} value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
          </Stack>
          <FileUploadField label="Quotation (optional)" accept=".pdf,image/*,.xls,.xlsx" value={quotationUrl} onChange={(url) => setQuotationUrl(url ?? '')} />

          {!seeded && (
            <>
              <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Material purchase details</Typography></Divider>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Description *</TableCell><TableCell>Unit</TableCell><TableCell align="right">Qty</TableCell><TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell><TableCell />
                </TableRow></TableHead>
                <TableBody>
                  {lines.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell><TextField size="small" variant="standard" fullWidth value={l.description} onChange={(e) => setLine(i, 'description', e.target.value)} /></TableCell>
                      <TableCell><TextField size="small" variant="standard" value={l.unit} onChange={(e) => setLine(i, 'unit', e.target.value)} sx={{ width: 60 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} sx={{ width: 70 }} /></TableCell>
                      <TableCell align="right"><TextField size="small" variant="standard" type="number" value={l.rate} onChange={(e) => setLine(i, 'rate', e.target.value)} sx={{ width: 90 }} /></TableCell>
                      <TableCell align="right">{inr((Number(l.quantity) || 0) * (Number(l.rate) || 0))}</TableCell>
                      <TableCell><IconButton size="small" color="error" onClick={() => setLines((arr) => arr.filter((_, j) => j !== i))} disabled={lines.length === 1}><Delete fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button size="small" startIcon={<Add />} onClick={() => setLines((arr) => [...arr, emptyLpoLine()])}>Add material</Button>
                <Typography variant="subtitle1">Total: {inr(total)}</Typography>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave || mut.isPending} onClick={() => mut.mutate()}>Create LPO</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, onClose, onChanged, onToast }: { id: number; onClose: () => void; onChanged: () => void; onToast: (m: string) => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const { data: po } = useQuery({ queryKey: ['epc-po', id], queryFn: () => epcPoApi.get(id) });
  const { data: grns } = useQuery({ queryKey: ['epc-po-grns', id], queryFn: () => epcPoApi.grns(id), enabled: Boolean(po) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-po', id] }); qc.invalidateQueries({ queryKey: ['epc-po-grns', id] }); onChanged(); };
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { refresh(); onToast(msg); }).catch((e) => setErr(extractApiError(e)));

  const [it, setIt] = useState({ description: '', unit: '', quantity: '', rate: '' });
  const addItem = useMutation({
    mutationFn: () => epcPoApi.addItem(id, { description: it.description, unit: it.unit || undefined, quantity: Number(it.quantity), rate: Number(it.rate) }),
    onSuccess: () => { setIt({ description: '', unit: '', quantity: '', rate: '' }); refresh(); },
    onError: (e) => setErr(extractApiError(e)),
  });

  if (!po) return null;
  const isDraft = po.status === 'draft';
  const canEdit = isDraft && hasPermission('epc_purchase_order.update');
  const canReceive = (po.status === 'issued' || po.status === 'partially_received') && hasPermission('epc_purchase_order.receive');
  const canManage = hasPermission('epc_purchase_order.update');

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{po.poCode}
        {po.lpoStage && <Chip size="small" sx={{ ml: 1 }} label={po.lpoStage} color={lpoStageColor(po.lpoStage)} />}
        <Chip size="small" variant="outlined" sx={{ ml: 1 }} label={po.status.replace('_', ' ')} color={statusColor(po.status)} />
        <Typography variant="body2" color="text.secondary">
          Project #{po.projectId} · {po.supplierName ?? po.vendorName ?? 'Local shop'}
          {po.shopPhone ? ` (${po.shopPhone})` : ''} · Total {inr(po.totalAmount)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>Description</TableCell><TableCell align="right">Qty</TableCell><TableCell>Unit</TableCell>
            <TableCell align="right">Rate</TableCell><TableCell align="right">Amount</TableCell><TableCell align="right">Received</TableCell>{canEdit && <TableCell />}
          </TableRow></TableHead>
          <TableBody>
            {(po.items ?? []).map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.description}</TableCell><TableCell align="right">{row.quantity}</TableCell><TableCell>{row.unit ?? '—'}</TableCell>
                <TableCell align="right">{inr(row.rate)}</TableCell><TableCell align="right">{inr(row.amount)}</TableCell>
                <TableCell align="right">{row.receivedQty}{row.receivedQty >= row.quantity ? ' ✓' : ''}</TableCell>
                {canEdit && <TableCell><IconButton size="small" color="error" onClick={() => act(() => epcPoApi.removeItem(id, row.id), 'Item removed')}><Delete fontSize="small" /></IconButton></TableCell>}
              </TableRow>
            ))}
            {(po.items ?? []).length === 0 && <TableRow><TableCell colSpan={canEdit ? 7 : 6}><Typography variant="body2" color="text.secondary">No items yet.</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>

        {canEdit && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <TextField size="small" label="Description" value={it.description} onChange={(e) => setIt({ ...it, description: e.target.value })} sx={{ minWidth: 200 }} />
              <TextField size="small" label="Qty" type="number" value={it.quantity} onChange={(e) => setIt({ ...it, quantity: e.target.value })} sx={{ width: 90 }} />
              <TextField size="small" label="Unit" value={it.unit} onChange={(e) => setIt({ ...it, unit: e.target.value })} sx={{ width: 90 }} />
              <TextField size="small" label="Rate" type="number" value={it.rate} onChange={(e) => setIt({ ...it, rate: e.target.value })} sx={{ width: 110 }} />
              <Button startIcon={<Add />} variant="outlined" disabled={!it.description || !it.quantity || !it.rate || addItem.isPending} onClick={() => addItem.mutate()}>Add</Button>
            </Stack>
          </>
        )}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Documents & Payment</Typography>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 90 }}>Quotation</Typography>
            {po.quotationUrl ? <Link href={po.quotationUrl} target="_blank" rel="noreferrer" variant="body2">View</Link> : <Typography variant="caption" color="text.secondary">Not attached</Typography>}
            {canManage && <Box sx={{ minWidth: 200 }}><FileUploadField accept=".pdf,image/*,.xls,.xlsx" value={po.quotationUrl ?? null} onChange={(url) => act(() => epcPoApi.setQuotation(id, url), 'Quotation updated')} /></Box>}
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" sx={{ minWidth: 90 }}>Invoice</Typography>
            {po.invoiceUrl
              ? <Link href={po.invoiceUrl} target="_blank" rel="noreferrer" variant="body2">View</Link>
              : <Chip size="small" color="warning" label="Invoice not received" />}
            {po.invoiceStatus === 'uploaded' && <Chip size="small" color="info" label="Verify" />}
            {po.invoiceStatus === 'verified' && <Chip size="small" color="success" label="Verified" />}
            {canManage && po.paymentStatus === 'released' && po.invoiceStatus !== 'verified' && (
              <Box sx={{ minWidth: 200 }}><FileUploadField accept=".pdf,image/*,.xls,.xlsx" value={po.invoiceUrl ?? null} onChange={(url) => { if (url) act(() => epcPoApi.uploadInvoice(id, url), 'Invoice uploaded'); }} /></Box>
            )}
          </Stack>
        </Stack>

        {(grns ?? []).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Goods receipts (GRN)</Typography>
            {(grns ?? []).map((g) => (
              <Typography key={g.id} variant="body2" color="text.secondary">
                {g.grnCode}{g.receivedDate ? ` · ${new Date(g.receivedDate).toLocaleDateString('en-IN')}` : ''} — {g.items.length} line(s)
              </Typography>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isDraft && hasPermission('epc_purchase_order.delete') && <Button color="error" onClick={() => act(() => epcPoApi.remove(id).then(() => onClose()), 'PO deleted')}>Delete</Button>}
        {/* LPO lifecycle: approve → payment request/approve/release → invoice verify */}
        {canManage && po.approvalStatus !== 'approved' && <Button variant="contained" onClick={() => act(() => epcPoApi.approveLpo(id), 'LPO approved — payment request raised')}>Approve LPO</Button>}
        {canManage && po.paymentStatus === 'requested' && <Button variant="contained" color="success" onClick={() => act(() => epcPoApi.approvePayment(id), 'Payment approved — pending for finance')}>Approve Payment</Button>}
        {canManage && po.paymentStatus === 'approved' && <Button variant="contained" color="success" onClick={() => act(() => epcPoApi.releasePayment(id), 'Payment released')}>Release Payment</Button>}
        {canManage && po.invoiceStatus === 'uploaded' && <Button variant="contained" color="success" onClick={() => act(() => epcPoApi.verifyInvoice(id), 'Invoice verified — request completed')}>Verify Invoice</Button>}
        {isDraft && hasPermission('epc_purchase_order.issue') && <Button onClick={() => act(() => epcPoApi.issue(id), 'PO issued to vendor')}>Issue (GRN)</Button>}
        {canReceive && <Button variant="contained" color="success" startIcon={<LocalShipping />} onClick={() => setReceiveOpen(true)}>Receive goods</Button>}
        {(po.status === 'issued' || po.status === 'partially_received' || po.status === 'draft') && hasPermission('epc_purchase_order.update') && (
          <Button color="error" onClick={() => { const r = window.prompt('Cancel reason (optional)?') ?? undefined; act(() => epcPoApi.cancel(id, r), 'PO cancelled'); }}>Cancel PO</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {receiveOpen && <ReceiveDialog poId={id} items={po.items ?? []} onClose={() => setReceiveOpen(false)} onDone={(msg) => { setReceiveOpen(false); refresh(); onToast(msg); }} onError={setErr} />}
    </Dialog>
  );
}

function ReceiveDialog({ poId, items, onClose, onDone, onError }: {
  poId: number; items: PoItem[]; onClose: () => void; onDone: (msg: string) => void; onError: (e: string) => void;
}) {
  const outstanding = items.filter((i) => i.receivedQty < i.quantity);
  const [qty, setQty] = useState<Record<number, string>>({});
  const [receivedDate, setReceivedDate] = useState('');
  const mut = useMutation({
    mutationFn: () => {
      const lines = outstanding
        .map((i) => ({ poItemId: i.id, receivedQty: Number(qty[i.id] || 0) }))
        .filter((l) => l.receivedQty > 0);
      if (!lines.length) throw new Error('Enter a received quantity for at least one item');
      return epcPoApi.receive(poId, { receivedDate: receivedDate || undefined, items: lines });
    },
    onSuccess: () => onDone('Goods received (GRN recorded)'),
    onError: (e) => onError(extractApiError(e)),
  });
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Receive goods</DialogTitle>
      <DialogContent dividers>
        <TextField label="Received date" type="date" size="small" InputLabelProps={{ shrink: true }} value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} sx={{ mb: 2 }} />
        <Table size="small">
          <TableHead><TableRow><TableCell>Item</TableCell><TableCell align="right">Outstanding</TableCell><TableCell align="right">Receive now</TableCell></TableRow></TableHead>
          <TableBody>
            {outstanding.map((i) => (
              <TableRow key={i.id}>
                <TableCell>{i.description}</TableCell>
                <TableCell align="right">{i.quantity - i.receivedQty} {i.unit ?? ''}</TableCell>
                <TableCell align="right">
                  <TextField size="small" type="number" value={qty[i.id] ?? ''} onChange={(e) => setQty({ ...qty, [i.id]: e.target.value })} sx={{ width: 100 }} />
                </TableCell>
              </TableRow>
            ))}
            {outstanding.length === 0 && <TableRow><TableCell colSpan={3}>Everything has been received.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending || outstanding.length === 0} onClick={() => mut.mutate()}>Confirm receipt</Button>
      </DialogActions>
    </Dialog>
  );
}
